import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Calculator,
  Package,
  Factory,
  FlagBanner,
  CheckCircle,
  Warning,
  ArrowRight,
  X,
  Prohibit,
  Sparkle,
  Copy,
} from "@phosphor-icons/react";
import RecipeCombobox from "@/components/RecipeCombobox";
import InventoryItemCombobox from "@/components/InventoryItemCombobox";

/**
 * ProductionRunWizard — single-screen guided flow:
 *   1. Setup   pick recipe + batch size + FG item, LIVE feasibility & cost preview
 *   2. Reserve lock the currently-available stock so nobody else drains it
 *   3. Issue   FIFO/FEFO auto-consume raw materials (irrevocable)
 *   4. Complete enter actual yield, compute variance, credit FG stock
 *
 * Backend endpoints used (all pre-existing):
 *   POST /api/inventory/batch-calculator/preview
 *   POST /api/inventory/production-runs                       → planned
 *   POST /api/inventory/production-runs/{id}/reserve          → reserved
 *   POST /api/inventory/production-runs/{id}/issue            → in_progress
 *   POST /api/inventory/production-runs/{id}/complete         → completed
 *   POST /api/inventory/production-runs/{id}/cancel
 *
 * A run's status determines the current step, so users can bookmark
 * /inventory/production-wizard/{runId} and resume where they left off.
 */

const STEPS = [
  { key: "setup", label: "Setup", icon: Calculator, statuses: [null, "planned"] },
  { key: "reserve", label: "Reserve", icon: Package, statuses: ["planned", "reserved"] },
  { key: "issue", label: "Issue", icon: Factory, statuses: ["reserved", "issuing", "in_progress"] },
  { key: "complete", label: "Complete", icon: FlagBanner, statuses: ["in_progress", "completed"] },
];

function statusToStepIdx(status) {
  if (!status) return 0;
  if (status === "cancelled") return -1;
  if (["planned"].includes(status)) return 1;             // ready to reserve
  if (["reserved"].includes(status)) return 2;            // ready to issue
  if (["issuing", "in_progress"].includes(status)) return 3;  // ready to complete
  if (status === "completed") return 3;                    // stay on complete step, show summary
  return 0;
}

// ────────────────────────────── SETUP STEP ──────────────────────────────

function SetupStep({ recipes, items, onCreated, initial, clonedFromCode, onClearClone }) {
  const [recipeId, setRecipeId] = useState(initial?.recipe_id || "");
  const [batchSize, setBatchSize] = useState(initial?.batch_size ?? 30);
  const [fgItemId, setFgItemId] = useState(initial?.fg_item_id || "");
  const [notes, setNotes] = useState(initial?.notes || "");
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [creating, setCreating] = useState(false);
  const debounceRef = useRef(null);

  // If the parent finishes loading the clone-source *after* first render,
  // hydrate the form once.
  const seededRef = useRef(false);
  useEffect(() => {
    if (initial && !seededRef.current) {
      seededRef.current = true;
      setRecipeId(initial.recipe_id || "");
      setBatchSize(initial.batch_size ?? 30);
      setFgItemId(initial.fg_item_id || "");
      setNotes(initial.notes || "");
    }
  }, [initial]);

  // Live preview on debounce whenever recipe or batch size changes.
  const runPreview = useCallback(async () => {
    if (!recipeId || !Number(batchSize)) {
      setPreview(null);
      return;
    }
    setPreviewing(true);
    try {
      const { data } = await api.post("/inventory/batch-calculator/preview", {
        recipe_id: recipeId,
        batch_size: Number(batchSize),
      });
      setPreview(data);
    } catch (e) {
      setPreview(null);
    } finally {
      setPreviewing(false);
    }
  }, [recipeId, batchSize]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(runPreview, 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [runPreview]);

  const create = async () => {
    if (!recipeId || Number(batchSize) <= 0) {
      toast.error("Pick a recipe and batch size first.");
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post("/inventory/production-runs", {
        recipe_id: recipeId,
        batch_size: Number(batchSize),
        fg_item_id: fgItemId || "",
        notes,
      });
      toast.success(`Run ${data.code} planned ✓`);
      onCreated(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Plan failed");
    } finally {
      setCreating(false);
    }
  };

  const canPlan =
    recipeId && Number(batchSize) > 0 && preview && preview.can_produce;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6" data-testid="wizard-setup">
      {/* Inputs */}
      <div className="lg:col-span-2 space-y-4">
        {clonedFromCode && (
          <div
            className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2.5"
            data-testid="wiz-clone-banner"
          >
            <Copy size={14} weight="duotone" className="text-emerald-700 mt-0.5 shrink-0" />
            <div className="flex-1 text-xs text-emerald-800">
              <div className="font-semibold">Cloned from {clonedFromCode}</div>
              <div className="opacity-80 mt-0.5">
                Recipe, batch size and FG item pre-filled. Change anything before planning.
              </div>
            </div>
            {onClearClone && (
              <button
                onClick={onClearClone}
                className="text-emerald-700 hover:text-emerald-900 shrink-0"
                data-testid="wiz-clone-clear"
                title="Start from scratch"
              >
                <X size={14} weight="bold" />
              </button>
            )}
          </div>
        )}
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">
            Recipe *
          </label>
          <RecipeCombobox
            recipes={recipes}
            value={recipeId}
            onPick={setRecipeId}
            testId="wiz-recipe"
            placeholder="Select recipe…"
            className="h-10"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">
            Batch size (kg of finished product) *
          </label>
          <input
            type="number"
            step="0.1"
            min="0.1"
            value={batchSize}
            onChange={(e) => setBatchSize(e.target.value)}
            className={inp}
            data-testid="wiz-batch-size"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">
            Finished-goods item (optional)
          </label>
          <InventoryItemCombobox
            items={items}
            value={fgItemId}
            onPick={setFgItemId}
            testId="wiz-fg-item"
            placeholder="— Don't credit FG stock —"
            className="h-10"
            showAvailability={false}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">
            Notes
          </label>
          <textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className={inp}
          />
        </div>

        <button
          onClick={create}
          disabled={!canPlan || creating}
          className="w-full h-11 rounded-md bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center justify-center gap-2"
          data-testid="wiz-plan-btn"
        >
          {creating ? "Planning…" : (
            <>
              <Sparkle size={16} weight="bold" />
              Plan Run &nbsp;<ArrowRight size={14} weight="bold" />
            </>
          )}
        </button>
        {preview && !preview.can_produce && (
          <p className="text-xs text-rose-600 flex items-center gap-1">
            <Warning size={12} weight="bold" />
            Restock the shorted items before planning this batch.
          </p>
        )}
      </div>

      {/* Live preview */}
      <div className="lg:col-span-3">
        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden min-h-[420px]">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Calculator size={14} weight="duotone" className="text-emerald-600" />
              <span className="text-sm font-semibold text-slate-900">Live feasibility preview</span>
            </div>
            {previewing && <span className="text-[10px] text-slate-400">Calculating…</span>}
          </div>
          {!preview && (
            <div className="p-10 text-center text-sm text-slate-400">
              Pick a recipe + batch size to see the required materials, live shortages and estimated batch cost.
            </div>
          )}
          {preview && (
            <>
              <div className="p-4 grid grid-cols-3 gap-3">
                <MiniTile label="Est. Cost" value={`₹${Number(preview.estimated_cost).toLocaleString("en-IN")}`} />
                <MiniTile label="Total Required" value={`${preview.total_required} kg`} />
                <MiniTile
                  label={preview.can_produce ? "Ready to produce" : "Shortage"}
                  value={preview.can_produce ? "✓" : `-${preview.total_shortage} kg`}
                  tone={preview.can_produce ? "emerald" : "rose"}
                />
              </div>
              <div className="max-h-72 overflow-y-auto border-t border-slate-100">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2">Ingredient</th>
                      <th className="text-right px-3 py-2">Required</th>
                      <th className="text-right px-3 py-2">Available</th>
                      <th className="text-right px-3 py-2">Shortage</th>
                      <th className="text-right px-3 py-2">Line ₹</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.items.map((it, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-3 py-2">{it.ingredient_name}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{it.required} {it.uom}</td>
                        <td className={`px-3 py-2 text-right tabular-nums ${it.available <= 0 ? "text-rose-600" : "text-slate-700"}`}>
                          {it.available} {it.uom}
                        </td>
                        <td className={`px-3 py-2 text-right tabular-nums font-semibold ${it.shortage > 0 ? "text-rose-600" : "text-emerald-600"}`}>
                          {it.shortage > 0 ? `-${it.shortage}` : "0"}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-500">₹{it.line_cost}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────── RESERVE STEP ──────────────────────────────

function ReserveStep({ run, onDone, onCancel }) {
  const [busy, setBusy] = useState(false);
  const act = async (endpoint) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/inventory/production-runs/${run.id}/${endpoint}`);
      toast.success(endpoint === "reserve" ? "Materials reserved ✓" : "Run cancelled");
      onDone(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-5" data-testid="wizard-reserve">
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-5">
        <div className="text-sm font-semibold text-amber-900 mb-1">Reserve the raw materials</div>
        <p className="text-xs text-amber-800 leading-relaxed">
          Reservation snapshots the current stock levels for this batch, so no
          one else can drain them before you issue. Reserve now, or cancel this
          plan and try a different batch size.
        </p>
      </div>
      <MaterialsTable materials={run.materials} showLive />
      <div className="flex flex-wrap gap-3 justify-between">
        <button
          onClick={() => act("cancel")}
          disabled={busy}
          className="h-10 px-4 rounded-md border border-slate-200 bg-white text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50 inline-flex items-center gap-1.5"
          data-testid="wiz-cancel-btn"
        >
          <Prohibit size={14} /> Cancel Plan
        </button>
        <button
          onClick={() => act("reserve")}
          disabled={busy}
          className="h-10 px-5 rounded-md bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-50 inline-flex items-center gap-1.5"
          data-testid="wiz-reserve-btn"
        >
          <Package size={14} weight="bold" />
          {busy ? "Reserving…" : "Reserve Materials"} <ArrowRight size={12} />
        </button>
      </div>
    </div>
  );
}

// ────────────────────────────── ISSUE STEP ──────────────────────────────

function IssueStep({ run, onDone }) {
  const [busy, setBusy] = useState(false);
  const issue = async () => {
    if (!window.confirm(
      "This will DEDUCT stock via FIFO/FEFO. Ingredient batches consumed cannot be undone. Proceed?"
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/inventory/production-runs/${run.id}/issue`);
      toast.success("Materials issued ✓");
      onDone(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Issue failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-5" data-testid="wizard-issue">
      <div className="rounded-lg border border-sky-200 bg-sky-50 p-5">
        <div className="text-sm font-semibold text-sky-900 mb-1">Issue materials to the floor</div>
        <p className="text-xs text-sky-800 leading-relaxed">
          The system will deduct each raw material FIFO/FEFO (first-in-first-out
          + earliest-expiry-first), write ledger rows, and lock the batch for
          this run. This action is irrevocable.
        </p>
      </div>
      <MaterialsTable materials={run.materials} showLive />
      <div className="flex justify-end">
        <button
          onClick={issue}
          disabled={busy}
          className="h-10 px-5 rounded-md bg-sky-600 text-white text-sm font-semibold hover:bg-sky-700 disabled:opacity-50 inline-flex items-center gap-1.5"
          data-testid="wiz-issue-btn"
        >
          <Factory size={14} weight="bold" />
          {busy ? "Issuing…" : "Issue Materials"} <ArrowRight size={12} />
        </button>
      </div>
    </div>
  );
}

// ────────────────────────────── COMPLETE STEP ──────────────────────────────

function CompleteStep({ run, onDone, navigate }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    actual_yield: run.actual_yield || run.batch_size,
    fg_batch_no: run.fg_batch_no || "",
    fg_expiry_date: run.fg_expiry_date || "",
    notes: "",
  });
  const isDone = run.status === "completed";

  const submit = async () => {
    if (Number(form.actual_yield) <= 0) return toast.error("Actual yield must be > 0");
    setBusy(true);
    try {
      const { data } = await api.post(`/inventory/production-runs/${run.id}/complete`, {
        actual_yield: Number(form.actual_yield),
        fg_batch_no: form.fg_batch_no || "",
        fg_expiry_date: form.fg_expiry_date || "",
        notes: form.notes || "",
      });
      toast.success(`Run ${data.code} completed 🎉`);
      onDone(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Complete failed");
    } finally {
      setBusy(false);
    }
  };

  const planned = Number(run.batch_size);
  const actual = Number(form.actual_yield);
  const variance = planned > 0 ? (((actual - planned) / planned) * 100).toFixed(2) : 0;
  const varianceGood = Math.abs(Number(variance)) <= 5;

  return (
    <div className="space-y-5" data-testid="wizard-complete">
      {isDone ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-5">
          <div className="flex items-center gap-2 text-emerald-800 font-semibold mb-1">
            <CheckCircle size={16} weight="fill" /> Run completed
          </div>
          <div className="text-xs text-emerald-800">
            Actual yield <span className="font-semibold">{run.actual_yield} kg</span>
            {" · "}Variance <span className="font-semibold">{run.yield_variance_pct}%</span>
            {" · "}Material cost <span className="font-semibold">₹{run.actual_material_cost}</span>
            {run.fg_item_id && run.fg_unit_cost !== undefined && (
              <> · FG unit cost <span className="font-semibold">₹{run.fg_unit_cost}</span></>
            )}
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-5">
          <div className="text-sm font-semibold text-emerald-900 mb-1">Record actual yield & close the run</div>
          <p className="text-xs text-emerald-800 leading-relaxed">
            Enter the actual kg you produced. We&apos;ll compute yield variance vs
            plan and (if you set an FG item at Setup) credit the finished-goods
            batch to inventory with a computed unit cost = material cost / actual yield.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Planned</div>
          <div className="text-3xl font-bold text-slate-900 tabular-nums mt-1">{planned} kg</div>
          <div className="text-[10px] text-slate-500 mt-2">Material cost ₹{run.total_material_cost || 0}</div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Actual yield</div>
          {isDone ? (
            <div className="text-3xl font-bold text-slate-900 tabular-nums mt-1">{run.actual_yield} kg</div>
          ) : (
            <input
              type="number"
              step="0.1"
              min="0"
              value={form.actual_yield}
              onChange={(e) => setForm((f) => ({ ...f, actual_yield: e.target.value }))}
              className="mt-1 w-full h-12 px-3 text-2xl font-bold bg-transparent focus:outline-none border-b-2 border-slate-200 focus:border-emerald-500"
              data-testid="wiz-actual-yield"
            />
          )}
          <div className={`text-[11px] font-semibold mt-2 tabular-nums ${varianceGood ? "text-emerald-600" : "text-rose-600"}`}>
            Variance {variance > 0 ? "+" : ""}{variance}%
          </div>
        </div>
      </div>

      {!isDone && run.fg_item_id && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">FG batch #</label>
            <input
              value={form.fg_batch_no}
              onChange={(e) => setForm((f) => ({ ...f, fg_batch_no: e.target.value }))}
              placeholder={`FG-${run.code}-${new Date().toISOString().slice(0, 10).replace(/-/g, "")}`}
              className={inp + " mt-1"}
              data-testid="wiz-fg-batch"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">FG expiry</label>
            <input
              type="date"
              value={form.fg_expiry_date}
              onChange={(e) => setForm((f) => ({ ...f, fg_expiry_date: e.target.value }))}
              className={inp + " mt-1"}
              data-testid="wiz-fg-expiry"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Notes</label>
            <input
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              className={inp + " mt-1"}
            />
          </div>
        </div>
      )}

      <div className="flex justify-between">
        <button
          onClick={() => navigate(`/inventory/production-runs/${run.id}`)}
          className="h-10 px-4 rounded-md border border-slate-200 bg-white text-sm text-slate-700 hover:bg-slate-50"
          data-testid="wiz-view-detail"
        >
          View full detail →
        </button>
        {!isDone && (
          <button
            onClick={submit}
            disabled={busy}
            className="h-10 px-5 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="wiz-complete-btn"
          >
            <FlagBanner size={14} weight="bold" />
            {busy ? "Completing…" : "Complete Run"}
          </button>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────── SHARED ──────────────────────────────

function MaterialsTable({ materials, showLive }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
      <div className="max-h-80 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 sticky top-0">
            <tr>
              <th className="text-left px-3 py-2">Ingredient</th>
              <th className="text-right px-3 py-2">Required</th>
              {showLive && <th className="text-right px-3 py-2">Available (plan)</th>}
              <th className="text-right px-3 py-2">Line ₹</th>
            </tr>
          </thead>
          <tbody>
            {materials.map((m, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="px-3 py-2 text-slate-900">{m.ingredient_name}</td>
                <td className="px-3 py-2 text-right tabular-nums">{m.required} {m.uom}</td>
                {showLive && (
                  <td className={`px-3 py-2 text-right tabular-nums ${
                    m.shortage_at_plan > 0 ? "text-rose-600" : "text-slate-700"
                  }`}>
                    {m.available_at_plan} {m.uom}
                  </td>
                )}
                <td className="px-3 py-2 text-right tabular-nums text-slate-500">₹{m.line_cost}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MiniTile({ label, value, tone = "slate" }) {
  const toneMap = {
    emerald: "bg-emerald-50 text-emerald-700",
    rose: "bg-rose-50 text-rose-700",
    slate: "bg-slate-50 text-slate-700",
  };
  return (
    <div className={`rounded-md p-3 ${toneMap[tone]}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-80 font-semibold">{label}</div>
      <div className="text-lg font-bold mt-0.5 tabular-nums">{value}</div>
    </div>
  );
}

// ────────────────────────────── MAIN ──────────────────────────────

export default function ProductionRunWizard() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const cloneId = searchParams.get("clone") || "";
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState([]);
  const [items, setItems] = useState([]);
  const [run, setRun] = useState(null);
  const [cloneSource, setCloneSource] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadMasters = useCallback(async () => {
    const [r, i] = await Promise.all([
      api.get("/inventory/recipes"),
      api.get("/inventory/items"),
    ]);
    setRecipes(r.data || []);
    setItems(i.data || []);
  }, []);

  const loadRun = useCallback(async () => {
    if (!id) {
      setRun(null);
      return;
    }
    try {
      const { data } = await api.get(`/inventory/production-runs/${id}`);
      setRun(data);
    } catch {
      toast.error("Run not found — starting fresh");
      navigate("/inventory/production-wizard", { replace: true });
    }
  }, [id, navigate]);

  const loadCloneSource = useCallback(async () => {
    if (!cloneId || id) {
      // Never overlay a clone banner on an existing run
      setCloneSource(null);
      return;
    }
    try {
      const { data } = await api.get(`/inventory/production-runs/${cloneId}`);
      setCloneSource({
        code: data.code,
        recipe_id: data.recipe_id,
        recipe_name: data.recipe_name,
        batch_size: data.batch_size,
        fg_item_id: data.fg_item_id || "",
        notes: data.notes ? `Cloned from ${data.code} · ${data.notes}` : `Cloned from ${data.code}`,
      });
    } catch {
      toast.error("Couldn't load the source run — starting fresh");
      setSearchParams({}, { replace: true });
    }
  }, [cloneId, id, setSearchParams]);

  const clearClone = useCallback(() => {
    setCloneSource(null);
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadMasters();
      await Promise.all([loadRun(), loadCloneSource()]);
      setLoading(false);
    })();
  }, [loadMasters, loadRun, loadCloneSource]);

  const stepIdx = useMemo(() => statusToStepIdx(run?.status), [run?.status]);

  const onCreated = (created) => {
    setRun(created);
    navigate(`/inventory/production-wizard/${created.id}`, { replace: true });
  };

  const onRunUpdated = (updated) => {
    setRun(updated);
    if (updated.status === "cancelled") {
      toast.info("Plan cancelled");
      navigate("/inventory/production-wizard", { replace: true });
      setRun(null);
    }
  };

  if (loading) return <div className="p-6 text-sm text-slate-500">Loading…</div>;

  return (
    <div className="p-6 space-y-6" data-testid="production-wizard">
      {/* Header + Stepper */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-600 font-semibold">
            Production · Wizard
          </div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">
            {run ? run.recipe_name : "New Production Run"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {run ? (
              <>
                <span className="font-mono">{run.code}</span> · planned batch{" "}
                <span className="font-medium text-slate-700">{run.batch_size} kg</span>
              </>
            ) : (
              "Plan → reserve → issue → complete in one guided flow."
            )}
          </p>
        </div>
        {run && (
          <button
            onClick={() => navigate("/inventory/production-runs")}
            className="text-sm text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
          >
            <X size={12} /> Back to runs list
          </button>
        )}
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2 md:gap-4 overflow-x-auto pb-2">
        {STEPS.map((s, i) => {
          const active = i === stepIdx;
          const done = i < stepIdx;
          const Icon = s.icon;
          return (
            <React.Fragment key={s.key}>
              <div
                className={
                  "inline-flex items-center gap-2 px-3 h-9 rounded-full border text-xs font-semibold whitespace-nowrap " +
                  (active
                    ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                    : done
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-white text-slate-500 border-slate-200")
                }
                data-testid={`wiz-step-${s.key}`}
              >
                <span className={"w-5 h-5 rounded-full inline-flex items-center justify-center text-[10px] font-bold " + (
                  active ? "bg-white/20" : done ? "bg-emerald-200" : "bg-slate-100"
                )}>
                  {done ? <CheckCircle size={11} weight="bold" /> : <Icon size={11} weight="bold" />}
                </span>
                {s.label}
              </div>
              {i < STEPS.length - 1 && (
                <div className={"h-px flex-1 min-w-6 " + (i < stepIdx ? "bg-emerald-400" : "bg-slate-200")} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Step body */}
      {!run && (
        <SetupStep
          recipes={recipes}
          items={items}
          onCreated={onCreated}
          initial={cloneSource || undefined}
          clonedFromCode={cloneSource?.code || ""}
          onClearClone={cloneSource ? clearClone : undefined}
        />
      )}
      {run && run.status === "planned" && (
        <ReserveStep run={run} onDone={onRunUpdated} onCancel={onRunUpdated} />
      )}
      {run && ["reserved"].includes(run.status) && (
        <IssueStep run={run} onDone={onRunUpdated} />
      )}
      {run && ["issuing", "in_progress", "completed"].includes(run.status) && (
        <CompleteStep run={run} onDone={onRunUpdated} navigate={navigate} />
      )}
      {run && run.status === "cancelled" && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-5 text-sm text-rose-800" data-testid="wizard-cancelled">
          This run was cancelled. <button className="underline" onClick={() => { setRun(null); navigate("/inventory/production-wizard"); }}>Start a new one</button>.
        </div>
      )}
    </div>
  );
}

const inp = "w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500";
